import React, { useRef, useState, useEffect } from 'react';
import EventDetailsEditor from './EventDetailsEditor';
import PropertiesHeader from './PropertiesHeader';
import ParameterListContainer, { ParameterViewMode } from './ParameterListContainer'; // Import ParameterViewMode
import { EventSchema, UnifiedSchema, Parameter, FilterOptions } from '../../types/schema';

interface SchemaPropertiesPanelProps {
  unifiedSchema: UnifiedSchema | null;
  selectedEvent: EventSchema | null;
  isEditable: boolean;
  handleEventChange: (eventId: string, field: keyof EventSchema, value: string) => void;
  isEventNameEditing: boolean;
  setIsEventNameEditing: (isEditing: boolean) => void;
  isEventCodeNameEditing: boolean;
  setIsEventCodeNameEditing: (isEditing: boolean) => void;
  isEventDescriptionEditing: boolean;
  setIsEventDescriptionEditing: (isEditing: boolean) => void;
  eventCodeNameError: string | null;
  eventNameInputRef: React.RefObject<HTMLInputElement>;
  eventCodeNameInputRef: React.RefObject<HTMLInputElement>;
  eventDescriptionInputRef: React.RefObject<HTMLTextAreaElement>;
  filteredGlobalTreeNodes: Parameter[];
  filteredEventSpecificTreeNodes: Parameter[];
  filterOptions: FilterOptions;
  setFilterOptions: React.Dispatch<React.SetStateAction<FilterOptions>>;
  handleAddParameter: (eventId: string, parentParam: Parameter | null, targetScope: 'global' | 'eventSpecific') => void;
  showFilterPanel: boolean;
  globalAllNodesMap: Map<string, Parameter>;
  eventSpecificAllNodesMap: Map<string, Parameter>;
  toggleTreeNode: (path: string, type: 'global' | 'eventSpecific') => void;
  handleParameterChange: (paramId: string, updates: Partial<Parameter>) => void;
  handleDeleteParameter: (paramId: string, deleteFromAllEvents?: boolean) => void;
  paramToEditId: string | null;
  clearParamToEditId: () => void;
  activeEditingParamId: string | null;
  setActiveEditingParamId: React.Dispatch<React.SetStateAction<string | null>>;
  handleParameterAssociationChange: (propertyFullPath: string, eventId: string, isAssociated: boolean) => void;
  allEvents: EventSchema[];
}

const SchemaPropertiesPanel: React.FC<SchemaPropertiesPanelProps> = ({
  unifiedSchema,
  selectedEvent,
  isEditable,
  handleEventChange,
  isEventNameEditing,
  setIsEventNameEditing,
  isEventCodeNameEditing,
  setIsEventCodeNameEditing,
  isEventDescriptionEditing,
  setIsEventDescriptionEditing,
  eventCodeNameError,
  eventNameInputRef,
  eventCodeNameInputRef,
  eventDescriptionInputRef,
  filteredGlobalTreeNodes,
  filteredEventSpecificTreeNodes,
  filterOptions,
  setFilterOptions,
  handleAddParameter,
  showFilterPanel,
  globalAllNodesMap,
  eventSpecificAllNodesMap,
  toggleTreeNode,
  handleParameterChange,
  handleDeleteParameter,
  paramToEditId,
  clearParamToEditId,
  activeEditingParamId,
  setActiveEditingParamId,
  handleParameterAssociationChange,
  allEvents,
}) => {
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [viewMode, setViewMode] = useState<ParameterViewMode>('tree'); // Add viewMode state

  useEffect(() => {
    const rightPanel = rightPanelRef.current;
    const stickyHeader = stickyHeaderRef.current;

    if (!rightPanel || !stickyHeader) return;

    const rightPanelPaddingTop = 24;

    const handleScroll = () => {
      const rightPanelRect = rightPanel.getBoundingClientRect();
      const stickyHeaderRect = stickyHeader.getBoundingClientRect();

      if (stickyHeaderRect.top <= rightPanelRect.top + rightPanelPaddingTop) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    rightPanel.addEventListener('scroll', handleScroll);
    return () => {
      rightPanel.removeEventListener('scroll', handleScroll);
    };
  }, [unifiedSchema, selectedEvent]); // Re-run if schema or selected event changes, as layout might shift

  return (
    <div ref={rightPanelRef} className="flex-1 p-4 md:p-6 flex flex-col gap-6 overflow-y-auto">
      {selectedEvent ? (
        <div className="w-full max-w-[1200px] mx-auto flex flex-col">
          {/* Event Details Section */}
          <EventDetailsEditor
            selectedEvent={selectedEvent}
            isEditable={isEditable}
            handleEventChange={handleEventChange}
            isEventNameEditing={isEventNameEditing}
            setIsEventNameEditing={setIsEventNameEditing}
            isEventCodeNameEditing={isEventCodeNameEditing}
            setIsEventCodeNameEditing={setIsEventCodeNameEditing}
            isEventDescriptionEditing={isEventDescriptionEditing}
            setIsEventDescriptionEditing={setIsEventDescriptionEditing}
            eventCodeNameError={eventCodeNameError}
            eventNameInputRef={eventNameInputRef}
            eventCodeNameInputRef={eventCodeNameInputRef}
            eventDescriptionInputRef={eventDescriptionInputRef}
          />

          {/* Sticky Header Container */}
          <div
            ref={stickyHeaderRef}
            className={`sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 pt-4 pb-2 mb-2 ${isSticky ? '' : ''}`}
            style={{ transform: isSticky ? 'translateY(-24px)' : 'translateY(0)' }}
          >
            {/* Filter, Add Property Controls, and Properties Header */}
            <PropertiesHeader
              // Calculate total filtered parameters including nested ones for the counter
              totalFilteredParametersCount={(() => {
                const countNodes = (nodes: Parameter[]): number => {
                  let count = 0;
                  nodes.forEach(node => {
                    count++;
                    if (node.children) {
                      count += countNodes(node.children);
                    }
                  });
                  return count;
                };
                return countNodes(filteredGlobalTreeNodes) + countNodes(filteredEventSpecificTreeNodes);
              })()}
              filterOptions={filterOptions}
              setFilterOptions={setFilterOptions}
              isEditable={isEditable}
              selectedEvent={selectedEvent}
              handleAddParameter={handleAddParameter}
              showFilterPanel={showFilterPanel}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          </div>

          {/* Properties List */}
          <ParameterListContainer
            unifiedSchema={unifiedSchema}
            selectedEvent={selectedEvent}
            isEditable={isEditable}
            filteredGlobalTreeNodes={filteredGlobalTreeNodes}
            filteredEventSpecificTreeNodes={filteredEventSpecificTreeNodes}
            filterOptions={filterOptions}
            globalAllNodesMap={globalAllNodesMap}
            eventSpecificAllNodesMap={eventSpecificAllNodesMap}
            toggleTreeNode={toggleTreeNode}
            handleParameterChange={handleParameterChange}
            handleDeleteParameter={handleDeleteParameter}
            handleAddParameter={handleAddParameter}
            paramToEditId={paramToEditId}
            clearParamToEditId={clearParamToEditId}
            activeEditingParamId={activeEditingParamId}
            setActiveEditingParamId={setActiveEditingParamId}
            allEvents={allEvents}
            handleParameterAssociationChange={handleParameterAssociationChange}
            viewMode={viewMode}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <p className="text-lg font-semibold mb-2">No event selected</p>
          <p className="text-sm">Please select an event from the left panel to view its schema properties.</p>
        </div>
      )}
    </div>
  );
};

export default SchemaPropertiesPanel;
